import { Dispatch } from 'redux'
import { budgetApi, IProjectBudget } from '../../../api/budgetApi'
import { IBudgetItem, IBudgetCategory, IBudgetFilters } from '../types/budget'
import { validateBudgetAcceptance } from '../utils/budgetValidation'
// No normalization - direct field mapping only per codebase policy

// Action Types
export const GET_BUDGET_ITEMS_REQUEST = 'GET_BUDGET_ITEMS_REQUEST'
export const GET_BUDGET_ITEMS_SUCCESS = 'GET_BUDGET_ITEMS_SUCCESS'
export const GET_BUDGET_ITEMS_FAILURE = 'GET_BUDGET_ITEMS_FAILURE'

export const GET_BUDGET_CATEGORIES_REQUEST = 'GET_BUDGET_CATEGORIES_REQUEST'
export const GET_BUDGET_CATEGORIES_SUCCESS = 'GET_BUDGET_CATEGORIES_SUCCESS'
export const GET_BUDGET_CATEGORIES_FAILURE = 'GET_BUDGET_CATEGORIES_FAILURE'

export const CREATE_BUDGET_ITEM_REQUEST = 'CREATE_BUDGET_ITEM_REQUEST'
export const CREATE_BUDGET_ITEM_SUCCESS = 'CREATE_BUDGET_ITEM_SUCCESS'
export const CREATE_BUDGET_ITEM_FAILURE = 'CREATE_BUDGET_ITEM_FAILURE'

export const UPDATE_BUDGET_ITEM_REQUEST = 'UPDATE_BUDGET_ITEM_REQUEST'
export const UPDATE_BUDGET_ITEM_SUCCESS = 'UPDATE_BUDGET_ITEM_SUCCESS'
export const UPDATE_BUDGET_ITEM_FAILURE = 'UPDATE_BUDGET_ITEM_FAILURE'

export const DELETE_BUDGET_ITEM_REQUEST = 'DELETE_BUDGET_ITEM_REQUEST'
export const DELETE_BUDGET_ITEM_SUCCESS = 'DELETE_BUDGET_ITEM_SUCCESS'
export const DELETE_BUDGET_ITEM_FAILURE = 'DELETE_BUDGET_ITEM_FAILURE'

export const SET_BUDGET_FILTERS = 'SET_BUDGET_FILTERS'
export const SET_BUDGET_LOADING = 'SET_BUDGET_LOADING'
export const CLEAR_BUDGET_ERROR = 'CLEAR_BUDGET_ERROR'
export const CLEAR_BUDGET_CREATION_STATE = 'CLEAR_BUDGET_CREATION_STATE'
export const MARK_BUDGET_NOTIFICATION_SHOWN = 'MARK_BUDGET_NOTIFICATION_SHOWN'

export const GET_BUDGETS_REQUEST = 'GET_BUDGETS_REQUEST'
export const GET_BUDGETS_SUCCESS = 'GET_BUDGETS_SUCCESS'
export const GET_BUDGETS_FAILURE = 'GET_BUDGETS_FAILURE'

export const CREATE_BUDGET_REQUEST = 'CREATE_BUDGET_REQUEST'
export const CREATE_BUDGET_SUCCESS = 'CREATE_BUDGET_SUCCESS'
export const CREATE_BUDGET_FAILURE = 'CREATE_BUDGET_FAILURE'

export const UPDATE_BUDGET_STATUS_REQUEST = 'UPDATE_BUDGET_STATUS_REQUEST'
export const UPDATE_BUDGET_STATUS_SUCCESS = 'UPDATE_BUDGET_STATUS_SUCCESS'
export const UPDATE_BUDGET_STATUS_FAILURE = 'UPDATE_BUDGET_STATUS_FAILURE'

export const ACCEPT_BUDGET_REQUEST = 'ACCEPT_BUDGET_REQUEST'
export const ACCEPT_BUDGET_SUCCESS = 'ACCEPT_BUDGET_SUCCESS'
export const ACCEPT_BUDGET_FAILURE = 'ACCEPT_BUDGET_FAILURE'

export const DELETE_BUDGET_REQUEST = 'DELETE_BUDGET_REQUEST'
export const DELETE_BUDGET_SUCCESS = 'DELETE_BUDGET_SUCCESS'
export const DELETE_BUDGET_FAILURE = 'DELETE_BUDGET_FAILURE'

// Action Creators
export interface IBudgetAction {
  type: string
  payload?: any
}

// Enhanced error message extraction helper
const extractErrorMessage = (error: any, fallbackMessage: string): string => {
  if (error.response?.data?.message) {
    return error.response.data.message
  }
  if (error.message) {
    return error.message
  }
  return fallbackMessage
}

// Budget Items Actions
export const getBudgetItems = (projectId?: string | number, filters?: IBudgetFilters) => {
  return async (dispatch: Dispatch<IBudgetAction>) => {
    dispatch({ type: GET_BUDGET_ITEMS_REQUEST })
    try {
      const response = projectId 
        ? await budgetApi.getBudgetItems(projectId, filters)
        : await budgetApi.getAllBudgetItems(filters)
      
      dispatch({ type: GET_BUDGET_ITEMS_SUCCESS, payload: response })
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to fetch budget items')
      dispatch({ type: GET_BUDGET_ITEMS_FAILURE, payload: errorMessage })
    }
  }
}

export const createBudgetItem = (projectId: string | number, data: Partial<IBudgetItem>) => {
  return async (dispatch: Dispatch<IBudgetAction>) => {
    dispatch({ type: CREATE_BUDGET_ITEM_REQUEST })
    try {
      const response = await budgetApi.createBudgetItem(projectId, data)
      dispatch({ type: CREATE_BUDGET_ITEM_SUCCESS, payload: response })
      return response
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to create budget item')
      dispatch({ type: CREATE_BUDGET_ITEM_FAILURE, payload: errorMessage })
      throw error
    }
  }
}

export const updateBudgetItem = (projectId: string | number, itemId: string | number, data: Partial<IBudgetItem>) => {
  return async (dispatch: Dispatch<IBudgetAction>) => {
    dispatch({ type: UPDATE_BUDGET_ITEM_REQUEST })
    try {
      const response = await budgetApi.updateBudgetItem(projectId, itemId, data)
      dispatch({ type: UPDATE_BUDGET_ITEM_SUCCESS, payload: response })
      return response
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to update budget item')
      dispatch({ type: UPDATE_BUDGET_ITEM_FAILURE, payload: errorMessage })
      throw error
    }
  }
}

export const deleteBudgetItem = (projectId: string | number, itemId: string | number) => {
  return async (dispatch: Dispatch<IBudgetAction>) => {
    dispatch({ type: DELETE_BUDGET_ITEM_REQUEST })
    try {
      await budgetApi.deleteBudgetItem(projectId, itemId)
      dispatch({ type: DELETE_BUDGET_ITEM_SUCCESS, payload: itemId })
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to delete budget item')
      dispatch({ type: DELETE_BUDGET_ITEM_FAILURE, payload: errorMessage })
      throw error
    }
  }
}

// Budget Categories Actions
export const getBudgetCategories = (projectId?: string | number, filters?: IBudgetFilters) => {
  return async (dispatch: Dispatch<IBudgetAction>) => {
    dispatch({ type: GET_BUDGET_CATEGORIES_REQUEST })
    try {
      const response = projectId 
        ? await budgetApi.getBudgetCategories(projectId, filters)
        : await budgetApi.getAllBudgetCategories(filters)
      dispatch({ type: GET_BUDGET_CATEGORIES_SUCCESS, payload: response })
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to fetch budget categories')
      dispatch({ type: GET_BUDGET_CATEGORIES_FAILURE, payload: errorMessage })
    }
  }
}

// Filter and UI Actions
export const setBudgetFilters = (filters: IBudgetFilters) => ({
  type: SET_BUDGET_FILTERS,
  payload: filters
})

export const setBudgetLoading = (loading: boolean) => ({
  type: SET_BUDGET_LOADING,
  payload: loading
})

export const clearBudgetError = () => ({
  type: CLEAR_BUDGET_ERROR
})

export const clearBudgetCreationState = () => ({
  type: CLEAR_BUDGET_CREATION_STATE
})

export const markBudgetNotificationShown = () => ({
  type: MARK_BUDGET_NOTIFICATION_SHOWN
})

// Budget Actions - Following existing Redux patterns from design document
export const getBudgets = (filters?: IBudgetFilters) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: GET_BUDGETS_REQUEST })
    try {
      // Load all pages of budget data automatically
      const allBudgets: IProjectBudget[] = []
      let currentPage = 1
      let totalPages = 1
      
      // Load first page to get pagination info
      const firstPageResponse = await budgetApi.getBudgets(filters)
      allBudgets.push(...firstPageResponse.results)
      totalPages = firstPageResponse.total_pages
      
      // Load remaining pages if there are any
      while (currentPage < totalPages) {
        currentPage++
        // Create new filters object with page parameter for subsequent pages
        const pageFilters = { ...filters, page: currentPage }
        const pageResponse = await budgetApi.getBudgets(pageFilters as any)
        allBudgets.push(...pageResponse.results)
      }
      
      // Create combined response with all budgets
      const combinedResponse = {
        ...firstPageResponse,
        results: allBudgets,
        count: allBudgets.length,
        current_page: 1,
        page_size: allBudgets.length,
        total_pages: 1,
        to: allBudgets.length,
        from: allBudgets.length > 0 ? 1 : 0,
        last_page: 1,
        links: {
          next: null,
          previous: null
        }
      }
      
      dispatch({ type: GET_BUDGETS_SUCCESS, payload: combinedResponse })
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to fetch budgets')
      dispatch({ type: GET_BUDGETS_FAILURE, payload: errorMessage })
    }
  }
}

// Helper function to transform frontend budget data to backend Project data structure
export const transformBudgetDataForBackend = (frontendData: any): any => {

  const transformed: any = {
    name: frontendData.name, // No fallback - direct mapping only
    client: frontendData.client, // No fallback - direct mapping only
    project_type: frontendData.project_type, // Direct mapping - backend now accepts sponsor/service_provider
    total_budget: frontendData.total_budget,
    status: frontendData.status as 'draft' | 'accepted' | 'declined',
    start_date: frontendData.start_date,
    end_date: frontendData.end_date,
    therapeutic_area: frontendData.therapeutic_area, // Direct mapping only
    study_type: frontendData.study_type, // Direct mapping only  
    study_phase: frontendData.study_phase, // Direct mapping only
    phases: frontendData.phases, // Direct mapping only - no defensive programming
    priority: frontendData.priority as 'high' | 'medium' | 'low',
    responsible_person: frontendData.responsible_person,
    department: frontendData.department,
    notes: frontendData.notes
  }
  
  // Only add health field if it's provided
  if (frontendData.health) {
    transformed.health = frontendData.health as 'on_track' | 'at_risk' | 'over_budget' | 'completed'
  }
  
  // Position data transformation - always include budget_positions
  if (frontendData.budget_positions !== undefined) {
    // Debug: Log raw budget_positions before transformation
    if (frontendData.budget_positions?.length > 0 && frontendData.budget_positions[0].department_roles?.length > 0) {
      const firstRole = frontendData.budget_positions[0].department_roles[0]
      console.log('🔍 [budgetActions] Raw budget_positions[0].department_roles[0] before transform:', {
        position_name: firstRole.position_name,
        phase_id: firstRole.phase_id,
        phase_id_type: typeof firstRole.phase_id,
        phase_id_value: JSON.stringify(firstRole.phase_id)
      })
    }

    // For budget_positions, check if it needs enhancement (new budgets)
    if (frontendData._isNewBudget) {
      // Preserve hour_data from categories for ALL budgets (both new and cloned)
      // Hour data comes populated from Internal Settings categories
      transformed.budget_positions = enhanceBudgetPositionsWithSnapshots(frontendData.budget_positions)
    } else {
      // Clean phase_id empty strings in department_roles
      transformed.budget_positions = frontendData.budget_positions.map((category: any, catIndex: number) => ({
        ...category,
        department_roles: (category.department_roles || []).map((role: any, roleIndex: number) => {
          // Convert empty string phase_id to null for schema compliance
          const cleanedRole = { ...role }
          if (cleanedRole.phase_id === '') {
            console.log(`🔧 [budgetActions] Converting empty phase_id to null at category ${catIndex}, role ${roleIndex}:`, {
              position_name: role.position_name,
              before: role.phase_id,
              after: null
            })
            cleanedRole.phase_id = null
          }
          return cleanedRole
        })
      }))

      // Debug: Log transformed budget_positions after cleaning
      if (transformed.budget_positions?.length > 0 && transformed.budget_positions[0].department_roles?.length > 0) {
        const firstRole = transformed.budget_positions[0].department_roles[0]
        console.log('✅ [budgetActions] Transformed budget_positions[0].department_roles[0] after cleaning:', {
          position_name: firstRole.position_name,
          phase_id: firstRole.phase_id,
          phase_id_type: typeof firstRole.phase_id,
          phase_id_value: JSON.stringify(firstRole.phase_id)
        })
      }
    }
  } else {
    // Ensure budget_positions is at least an empty array
    transformed.budget_positions = []
  }
  
  // Also support selected_positions for backward compatibility
  if (frontendData.selected_positions) {
    // For selected_positions, preserve the exact structure
    transformed.budget_positions = frontendData.selected_positions
  }
  
  // Handle external positions separately
  if (frontendData.external_positions !== undefined) {
    // For external_positions, check if it needs enhancement (new budgets)
    if (frontendData._isNewBudget) {
      transformed.external_positions = enhanceExternalPositionsWithSnapshots(frontendData.external_positions)
    } else {
      transformed.external_positions = frontendData.external_positions
    }
  } else {
    // Ensure external_positions is at least an empty array
    transformed.external_positions = []
  }
  
  // Preserve any additional metadata (like migration info)
  if (frontendData.migrated) {
    transformed.migrated = frontendData.migrated
  }
  if (frontendData.migration_metadata) {
    transformed.migration_metadata = frontendData.migration_metadata
  }
  if (frontendData.cloned_from) {
    transformed.cloned_from = frontendData.cloned_from
  }
  if (frontendData.clone_metadata) {
    transformed.clone_metadata = frontendData.clone_metadata
  }
  
  // Include unit_data if provided (contains unit definitions with UUIDs)
  if (frontendData.unit_data !== undefined) {
    transformed.unit_data = frontendData.unit_data
  }

  // Seed billing rates from a template rather than from the live admin defaults.
  // Write-only on the backend; `transformed` is an explicit whitelist, so it has
  // to be forwarded by name (issue #98).
  if (frontendData.source_template !== undefined) {
    transformed.source_template = frontendData.source_template
  }

  // Include calculation_figures if provided
  if (frontendData.calculation_figures !== undefined) {
    transformed.calculation_figures = frontendData.calculation_figures
  }

  // Include unit_hours_data if provided
  if (frontendData.unit_hours_data !== undefined) {
    transformed.unit_hours_data = frontendData.unit_hours_data
  }

  // Include billing_rates_data if provided
  if (frontendData.billing_rates_data !== undefined) {
    transformed.billing_rates_data = frontendData.billing_rates_data
  }

  // Add data integrity metadata
  transformed.data_integrity_version = '1.0'
  transformed.snapshot_date = new Date().toISOString()

  // OPTIMIZE: Strip hour_data from budget_positions before sending to reduce payload size
  // Backend will populate hour_data from BudgetCategoryDepartmentRole during creation
  // Only strip for brand new budgets. Cloned budgets carry the source budget's hours,
  // and template budgets carry the template's adjusted hours (issue #98) — stripping
  // either would silently refill them from Internal Settings defaults.
  const isClone = frontendData._isClone || false
  const isTemplate = frontendData._isTemplate || false
  if (frontendData._isNewBudget && !isClone && !isTemplate && transformed.budget_positions) {
    const originalSize = JSON.stringify(transformed.budget_positions).length

    transformed.budget_positions = transformed.budget_positions.map((category: any) => ({
      ...category,
      department_roles: (category.department_roles || []).map((role: any) => {
        const { hour_data, ...roleWithoutHourData } = role
        return roleWithoutHourData
      })
    }))

    const optimizedSize = JSON.stringify(transformed.budget_positions).length
    const reduction = ((originalSize - optimizedSize) / originalSize * 100).toFixed(1)

    console.log('🚀 [transformBudgetDataForBackend] Payload optimization:', {
      originalSize: `${(originalSize / 1024).toFixed(2)} KB`,
      optimizedSize: `${(optimizedSize / 1024).toFixed(2)} KB`,
      reduction: `${reduction}%`,
      message: 'hour_data will be populated by backend from Internal Settings'
    })
  }

  // Log what's being sent to backend
  console.log('🚀 [transformBudgetDataForBackend] Transforming data for API');
  console.log('🚀 [transformBudgetDataForBackend] budget_positions count:', transformed.budget_positions?.length);
  if (transformed.budget_positions?.length > 0) {
    const firstCategory = transformed.budget_positions[0];
    console.log('🚀 [transformBudgetDataForBackend] First category:', firstCategory.name);
    if (firstCategory.department_roles?.length > 0) {
      const firstRole = firstCategory.department_roles[0];
      console.log('🚀 [transformBudgetDataForBackend] First role:', {
        id: firstRole.id,
        position_name: firstRole.position_name,
        has_hour_data: !!firstRole.hour_data,
        hour_data_length: firstRole.hour_data?.length,
        hour_data_sample: firstRole.hour_data?.[0]
      });
    }
  }

  // Add flags for backend to handle hour_data appropriately
  if (frontendData._isNewBudget !== undefined) {
    transformed._isNewBudget = frontendData._isNewBudget
  }
  if (frontendData._isClone !== undefined) {
    transformed._isClone = frontendData._isClone
  }

  return transformed
}

import { ensureUUID } from '../../../utils/uuidHelpers'

// Helper function to ensure budget positions have complete data snapshots
const enhanceBudgetPositionsWithSnapshots = (positions: any): any => {
  if (!positions) return positions
  
  // Handle settings API format (has id, name, department_roles)
  if (Array.isArray(positions) && positions.length > 0 &&
      (positions[0].department_roles !== undefined ||
       (positions[0].id !== undefined && positions[0].name !== undefined))) {
    // This is already in settings API format, return as-is
    return positions.map((category: any) => ({
      ...category,
      id: category.id ? ensureUUID(category.id) : category.id,
      // Preserve department_roles and ensure enabled field is preserved or defaulted
      department_roles: (category.department_roles || []).map((role: any) => ({
        ...role,
        // Preserve enabled field if it exists, otherwise default to true for clone mode
        enabled: role.enabled !== undefined ? role.enabled : true
      }))
    }))
  }
  
  // Handle legacy category-based positions (budget_positions format) for backward compatibility
  if (Array.isArray(positions) && positions.length > 0 && positions[0].category_id !== undefined) {
    return positions.map((category: any) => ({
      ...category,
      category_id: category.category_id ? ensureUUID(category.category_id) : category.category_id,
      positions: category.positions ? enhanceBudgetPositionsWithSnapshots(category.positions) : []
    }))
  }
  
  // Handle array of positions (selected_positions format)
  if (Array.isArray(positions)) {
    return positions.map(position => {
      // If position_data doesn't exist or is minimal, enhance it
      if (!position.position_data) {
        position.position_data = {}
      }
      
      // Only add missing required fields, preserve existing data structure
      const enhancedPositionData = {
        ...position.position_data,
        // Only convert ID if it exists and add UUID version
        ...(position.position_data?.id !== undefined && {
          id_uuid: ensureUUID(position.position_data.id)
        }),
        ...(position.position_data?.department_id !== undefined && {
          department_id_uuid: ensureUUID(position.position_data.department_id)
        }),
        ...(position.position_data?.category_id !== undefined && {
          category_id_uuid: ensureUUID(position.position_data.category_id)
        }),
        ...(position.position_data?.unit_id !== undefined && {
          unit_id_uuid: ensureUUID(position.position_data.unit_id)
        }),
        // Preserve department and unit fields
        ...(position.position_data?.department !== undefined && {
          department: position.position_data.department
        }),
        ...(position.position_data?.unit !== undefined && {
          unit: position.position_data.unit
        }),
        // Preserve department_name and unit_name if they exist
        ...(position.position_data?.department_name && {
          department_name: position.position_data.department_name
        }),
        ...(position.position_data?.hours_per_unit === undefined && position.unit_price && {
          hours_per_unit: position.unit_price
        }),
        ...(position.position_data?.total_hours === undefined && position.unit_price && {
          total_hours: position.unit_price
        }),
        // Only add these if explicitly needed for new positions
        ...(position._requiresEnhancement && {
          is_active: position.position_data?.is_active !== false,
          is_deleted: position.position_data?.is_deleted || false,
          snapshot_date: position.position_data?.snapshot_date || new Date().toISOString()
        })
      }
      
      // Validate required fields - skip validation for already complete data
      // These errors might be from positions at a higher level that don't have position_data yet
      if (!position.position_data || Object.keys(position.position_data).length === 0) {
        if (!enhancedPositionData.department && !enhancedPositionData.department_name) {
          console.error('Missing department name for position:', position)
        }
        // Only validate unit_name if unit is not null
              }
      
      return {
        ...position,
        position_data: enhancedPositionData
      }
    })
  }
  
  return positions
}

// Helper function to ensure external positions have complete data snapshots
const enhanceExternalPositionsWithSnapshots = (positions: any): any => {
  if (!positions || !Array.isArray(positions)) return positions

  return positions.map(position => {
    // External positions MUST have required fields - validate
    if (!position.id) {
      console.error('External position missing required id field:', position)
      throw new Error(`External position missing required id field: ${position.position_name || 'unnamed'}`)
    }
    if (!position.position_name) {
      throw new Error('External position missing required position_name field')
    }

    // Ensure ID is a UUID
    const positionId = ensureUUID(position.id)

    // Return only schema-defined fields
    return {
      // Required fields
      id: positionId,
      position_name: position.position_name,
      supplier_name: position.supplier_name,

      // Optional fields - only include if they exist
      ...(position.position_key && { position_key: position.position_key }),
      ...(position.description && { description: position.description }),
      ...(position.unit !== undefined && { unit: position.unit }),      ...(position.unit_relationship && { unit_relationship: position.unit_relationship }),
      ...(position.quantity !== undefined && { quantity: position.quantity }),
      ...(position.price_per_unit !== undefined && { price_per_unit: position.price_per_unit }),
      ...(position.total_cost !== undefined && { total_cost: position.total_cost }),
      ...(position.status && { status: position.status }),
      ...(position.cost_type && { cost_type: position.cost_type }),
      ...(position.phase_id !== undefined && {
        phase_id: position.phase_id === '' ? null : position.phase_id
      }),
      ...(position.phase_name !== undefined && { phase_name: position.phase_name }),
      ...(position.start_date !== undefined && { start_date: position.start_date }),
      ...(position.end_date !== undefined && { end_date: position.end_date }),
      ...(position.created_at && { created_at: position.created_at }),
      ...(position.updated_at && { updated_at: position.updated_at }),

      // Default values for specific fields if not present
      enabled: position.enabled !== undefined ? position.enabled : true,
      is_deleted: position.is_deleted || false,
      snapshot_date: position.snapshot_date || new Date().toISOString()
    }
  })
}

export const createBudget = (data: any, options?: { showNotifications?: boolean }) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: CREATE_BUDGET_REQUEST })
    try {
      // Transform frontend data to backend format
      const backendData = transformBudgetDataForBackend(data)
      
      
      const response = await budgetApi.createBudget(backendData)
      dispatch({ type: CREATE_BUDGET_SUCCESS, payload: response })
      
      // FIXED: Refresh budget list from backend to ensure data consistency
      // This ensures the frontend shows actual saved data, not just optimistic updates
      try {
        dispatch(getBudgets() as any)
      } catch (refreshError) {
        console.warn('Failed to refresh budgets after creation:', refreshError)
        // Don't fail the creation if refresh fails - the optimistic update is still valid
      }
      
      return response
    } catch (error: any) {
      let errorMessage = 'Failed to create budget'
      let detailedErrors: string[] = []
      
      if (error.response?.data) {
        const errorData = error.response.data
        
        // Handle different error response formats
        if (errorData.message) {
          errorMessage = errorData.message
        }
        
        // Handle validation errors
        if (errorData.errors && typeof errorData.errors === 'object') {
          detailedErrors = Object.entries(errorData.errors).flatMap(([field, messages]) => {
            if (Array.isArray(messages)) {
              return messages.map(msg => `${field}: ${msg}`)
            }
            return [`${field}: ${messages}`]
          })
        }
        
        // Handle network/server errors
        if (error.response.status >= 500) {
          errorMessage = 'Server error occurred. Please try again later.'
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication required. Please log in again.'
        } else if (error.response.status === 403) {
          errorMessage = 'You do not have permission to create budgets.'
        } else if (error.response.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.'
        }
      } else if (error.name === 'NetworkError' || error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your internet connection.'
      } else if (error.name === 'TimeoutError' || error.message.includes('timeout')) {
        errorMessage = 'Request timeout. Please try again.'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      const enhancedError = {
        ...error,
        message: errorMessage,
        detailedErrors,
        originalError: error
      }
      
      dispatch({ type: CREATE_BUDGET_FAILURE, payload: enhancedError })
      throw enhancedError
    }
  }
}

export const updateBudgetStatus = (budgetId: string | number, status: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: UPDATE_BUDGET_STATUS_REQUEST })

    // ✅ FIX Layer 3A: Validate ID is not undefined/null
    if (budgetId === undefined || budgetId === null ||
        (typeof budgetId === 'string' && budgetId === '') ||
        (typeof budgetId === 'number' && isNaN(budgetId))) {
      const errorMessage = 'Cannot update status - budget ID is missing'
      console.error(`🚫 API Layer: Prevented status update for undefined budget ID: ${budgetId}`)
      dispatch({ type: UPDATE_BUDGET_STATUS_FAILURE, payload: errorMessage })
      throw new Error(errorMessage)
    }

    // ✅ FIX Layer 3B: Validate ID before API call for optimistic budgets
    const isOptimisticId = typeof budgetId === 'string' && budgetId.startsWith('optimistic_')

    if (isOptimisticId) {
      const errorMessage = 'Cannot update status of budget that is still being created'
      console.error(`🚫 API Layer: Prevented status update for optimistic budget ${budgetId}`)
      dispatch({ type: UPDATE_BUDGET_STATUS_FAILURE, payload: errorMessage })
      throw new Error(errorMessage)
    }

    try {
      const response = await budgetApi.updateBudgetStatus(budgetId, status)
      dispatch({ type: UPDATE_BUDGET_STATUS_SUCCESS, payload: response })
      // No need to refresh budget list - reducer handles local state update
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to update budget status')
      dispatch({ type: UPDATE_BUDGET_STATUS_FAILURE, payload: errorMessage })
    }
  }
}

/**
 * Accept budget with completion validation
 *
 * Direct field access - NO defensive programming per CLAUDE.md.
 * Uses pure validation functions from utils/budgetValidation.
 * Validates:
 * - Budget ID is valid (positive integer)
 * - Budget ID is not optimistic (not temporary)
 * - Budget completion is 100%
 *
 * @param budgetId - Budget ID to accept
 * @param completion - Budget completion percentage
 * @returns Promise resolving to updated budget
 * @throws Error if validation fails or API call fails
 *
 * @example
 * dispatch(acceptBudget(123, 100))
 *   .then(() => console.log('Budget accepted'))
 *   .catch(err => alert(err.message));
 */
export const acceptBudget = (budgetId: number, completion: number) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: ACCEPT_BUDGET_REQUEST })

    // Validate using pure function from utils
    const validation = validateBudgetAcceptance(budgetId, completion)
    if (!validation.isValid) {
      dispatch({ type: ACCEPT_BUDGET_FAILURE, payload: validation.error })
      throw new Error(validation.error)
    }

    try {
      const response = await budgetApi.acceptBudget(budgetId)
      dispatch({ type: ACCEPT_BUDGET_SUCCESS, payload: response })
      return response
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to accept budget')
      dispatch({ type: ACCEPT_BUDGET_FAILURE, payload: errorMessage })
      throw error
    }
  }
}

export const deleteBudget = (budgetId: string | number) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: DELETE_BUDGET_REQUEST })
    
    // ✅ FIX Layer 3A: Validate ID is not undefined/null
    if (budgetId === undefined || budgetId === null || 
        (typeof budgetId === 'string' && budgetId === '') ||
        (typeof budgetId === 'number' && isNaN(budgetId))) {
      const errorMessage = 'Cannot delete budget - budget ID is missing'
      console.error(`🚫 API Layer: Prevented deletion for undefined budget ID: ${budgetId}`)
      dispatch({ type: DELETE_BUDGET_FAILURE, payload: errorMessage })
      throw new Error(errorMessage)
    }
    
    // ✅ FIX Layer 3B: Validate ID before API call for optimistic budgets
    const isOptimisticId = typeof budgetId === 'string' && budgetId.startsWith('optimistic_')
    
    if (isOptimisticId) {
      const errorMessage = 'Cannot delete budget that is still being created'
      console.error(`🚫 API Layer: Prevented deletion for optimistic budget ${budgetId}`)
      dispatch({ type: DELETE_BUDGET_FAILURE, payload: errorMessage })
      throw new Error(errorMessage)
    }
    
    try {
      await budgetApi.deleteBudget(budgetId)
      dispatch({ type: DELETE_BUDGET_SUCCESS, payload: budgetId })
      
      // Refresh the budget list to ensure consistency with backend
      dispatch(getBudgets() as any)
    } catch (error: any) {
      // Handle 404 errors gracefully - if budget doesn't exist, treat as successful deletion
      // This handles the case where the budget was already soft-deleted (deactivated=true)
      if (error.response?.status === 404) {
        dispatch({ type: DELETE_BUDGET_SUCCESS, payload: budgetId })
        return // Exit early, don't dispatch failure
      }
      
      const errorMessage = extractErrorMessage(error, 'Failed to delete budget')
      dispatch({ type: DELETE_BUDGET_FAILURE, payload: errorMessage })
    }
  }
}

// Thunk Dispatch Type
export type BudgetThunkDispatchType = (action: any) => Promise<any> 